<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  ArrowLeft,
  Calendar,
  ChatDotRound,
  CircleCheckFilled,
  Clock,
  Document,
  EditPen,
  Promotion,
  Star,
  User,
  WarningFilled,
} from "@element-plus/icons-vue";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SocialFundRequestActionDialog from "@/components/shared/SocialFund/SocialFundRequestActionDialog.vue";
import SocialFundDocumentValidationDialog from "@/components/shared/SocialFund/SocialFundDocumentValidationDialog.vue";
import SocialFundAgreementUploadDialog from "@/components/shared/SocialFund/SocialFundAgreementUploadDialog.vue";
import SocialFundContactDialog from "@/components/shared/SocialFund/SocialFundContactDialog.vue";
import SocialFundClassificationDialog from "@/components/shared/SocialFund/SocialFundClassificationDialog.vue";
import SocialFundChangeCallPeriodDialog from "@/components/shared/SocialFund/SocialFundChangeCallPeriodDialog.vue";
import SocialFundMessageEditor from "@/components/shared/SocialFund/SocialFundMessageEditor.vue";
import SocialFundRichText from "@/components/shared/SocialFund/SocialFundRichText.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import { useChatScroll } from "@/app/composables/useChatScroll";
import SharedExpandableContent from "@/components/shared/SharedExpandableContent.vue";
import SocialFundApplicantChat from "@/components/shared/SocialFund/SocialFundApplicantChat.vue";
import { socialFundFeatures, tireOptions, isValidTires, normalizeEvaluationAmount, agreementLabel, agreementDates, apiErrorMessage } from "@/utils/socialFundV2";
import Notification from "@/utils/plugins/NotificationService";
import { useUiStore } from "@/app/stores/uiStore";
import { useAuthStore } from "@/app/stores/authStore";
import SocialFundApplicationService from "@/api/services/socialFundApplication.service";
import SocialFundApplicationTypeService from "@/api/services/socialFundApplicationType.service";
import SocialFundCallPeriodService from "@/api/services/socialFundCallPeriod.service";

import { socialFundRichTextContent } from "@/utils/socialFundRichText";
import { getSocialFundDaysInStatus, getSocialFundStatusStyle } from "@/utils/socialFundStatus";

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const authStore = useAuthStore();

const applicationId = computed(() => route.params.id);
const commentText = ref("");
const commentVisibleToDirector = ref(true);
const savingComment = ref(false);
const savingEvaluation = ref(false);
const actionBusy = ref(false);
const approvedTiresInput = ref(null);
const showDeleteDocument = ref(false);
const documentToDelete = ref(null);
const loadError = ref("");
let loadGeneration = 0;
let disposed = false;
onBeforeUnmount(() => { disposed = true; loadGeneration++; });
const showCommentButton = ref(false);
const editingCommentId = ref(null);
const showActionDialog = ref(false);
const actionDialogKind = ref("prepare-direction-approved");
const showDocumentValidationDialog = ref(false);
const selectedDocument = ref(null);
const selectedDocumentStatus = ref("validated");
const evaluationAmountInput = ref(null);
const showApplicantDialog = ref(false);
const isSavingApplicant = ref(false);
const applicantHistory = ref([]);
const showClassificationDialog = ref(false);
const isSavingClassification = ref(false);
const applicationTypeOptions = ref([]);
const showCallPeriodDialog = ref(false);
const isSavingCallPeriod = ref(false);
const callPeriodOptions = ref([]);
const callPeriodDuplicateWarning = ref(null);

const request = ref(null);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("es-ES") : "—");
const formatTime = (value) => (value ? new Date(value).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "");
const formatCurrency = (value) =>
  value === null || value === undefined || value === "" ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(value));

const uploadedByLabel = { applicant: "Solicitante", employee: "A51", director: "Dirección", administration: "Administración" };

const statusLabel = {
  validated: "Validado",
  pending: "Pendiente",
  rejected: "Rechazado",
};

const mapDocument = (doc) => ({
  id: doc.id,
  name: doc.originalName || doc.documentTypeLabel,
  documentType: doc.documentType,
  documentTypeLabel: doc.documentTypeLabel,
  originalName: doc.originalName,
  status: doc.validationStatus,
  rejectionReason: doc.rejectionReason,
  uploadedAt: formatDate(doc.createdAt),
  uploadedBy: uploadedByLabel[doc.uploadedBy] || doc.uploadedBy,
  otherDocumentDescription: doc.otherDocumentDescription,
  // FS-S01: contentUrl es ahora un endpoint autenticado, no una ruta
  // pública — se usa tal cual con Api() (blob), no como <a href>.
  downloadUrl: doc.contentUrl || null,
});

const mapComment = (comment) => ({
  id: comment.id,
  author: comment.authorName || "—",
  createdAt: comment.createdAt,
  comment: comment.content,
  authorEmployeeId: comment.authorEmployeeId,
  visibleToDirector: comment.visibleToDirector,
});

const mapHistoryItem = (entry) => ({
  status: entry.statusLabel,
  actor: entry.changedByName || "Sistema ConnectUs",
  date: formatDate(entry.dateStart),
  time: formatTime(entry.dateStart),
  statusCode: entry.status,
  note: entry.note,
});

const mapApplicantHistoryItem = (application) => ({
  id: application.id,
  reference: application.reference,
  call: application.callPeriod?.label || application.callPeriod?.year || "—",
  aidType: application.applicationType?.name || "—",
  amount: formatCurrency(application.requestedAmount),
  status: application.currentStatusLabel || "Sin estado",
  statusCode: application.currentStatusCode,
  createdAt: formatDate(application.createdAt),
});

const mapApplication = (data) => {
  const daysInStatus = getSocialFundDaysInStatus(data.statusHistories);

  return {
    id: data.id,
    reference: data.reference,
    agreementStatus: data.agreementStatus,
    agreementStatusLabel: data.agreementStatusLabel,
    agreementSignedByApplicantAt: data.agreementSignedByApplicantAt,
    agreementCountersignedAt: data.agreementCountersignedAt,
    statusCode: data.currentStatusCode,
    chatOpen: data.chatOpen !== false,
    status: data.currentStatusLabel,
    a51ResolutionCode: data.a51Resolution,
    a51ResolutionLabel: data.a51ResolutionLabel,
    receivedAt: formatDate(data.createdAt),
    call: data.callPeriod?.label || data.callPeriod?.year || "—",
    callPeriodId: data.callPeriod?.id ?? null,
    daysInStatus,
    applicant: {
      id: data.applicant?.id,
      name: data.applicant?.name,
      taxId: data.applicant?.taxId,
      locality: data.applicant?.locality,
      province: data.applicant?.province,
      representativeName: data.applicant?.representativeName,
      representativeTaxId: data.applicant?.representativeTaxId,
      email: data.contactEmail,
      phone: data.contactPhone,
      contactPerson: data.contactPerson,
    },
    application: {
      aidTypeCode: data.applicationType?.code,
      aidType: data.applicationType?.name,
      entityName: data.entityName,
      requestedAmountRaw: data.requestedAmount,
      requestedTires: data.requestedTires,
      approvedTires: data.approvedTires,
      requestedAmount: formatCurrency(data.requestedAmount),
      approvedAmount: formatCurrency(data.suggestedAmount),
      amountJustification: data.amountJustification,
      description: data.projectDescription,
    },
    documents: (data.documents || []).map(mapDocument),
    comments: (data.comments || []).map(mapComment),
    history: (data.statusHistories || [])
      .slice()
      .sort((a, b) => new Date(b.dateStart) - new Date(a.dateStart))
      .map(mapHistoryItem),
  };
};

const loadApplicantHistory = async (applicantId, id, generation) => {
  if (!applicantId) return;
  try {
    const res = await SocialFundApplicationService.getApplications({ "applicant.id": applicantId, itemsPerPage: 100, "order[createdAt]": "DESC" });
    if (disposed || generation !== loadGeneration) return;
    applicantHistory.value = (res.data?.["hydra:member"] || []).filter(item => String(item.id) !== String(id)).map(mapApplicantHistoryItem);
  } catch (err) {
    if (generation === loadGeneration) Notification.addNotification(await apiErrorMessage(err, "No se ha podido cargar el histórico del solicitante."), "error", 6000);
  }
};
const loadRequest = async () => {
  const id = applicationId.value;
  const generation = ++loadGeneration;
  loadError.value = "";
  uiStore.startLoading();
  try {
    const res = await SocialFundApplicationService.getApplication(id);
    if (disposed || generation !== loadGeneration) return;
    if (!res?.data) throw new Error("Respuesta sin solicitud.");
    request.value = mapApplication(res.data);
    evaluationAmountFocused.value = false;
    evaluationAmountInput.value = res.data.suggestedAmount ?? null;
    approvedTiresInput.value = res.data.approvedTires ?? null;
    applicantHistory.value = [];
    await loadApplicantHistory(res.data.applicant?.id, id, generation);
  } catch (err) {
    if (disposed || generation !== loadGeneration) return;
    request.value = null;
    loadError.value = await apiErrorMessage(err, "No se ha podido cargar la solicitud de Fondo Social.");
  } finally { uiStore.stopLoading(); }
};

const loadApplicationTypes = async () => {
  const res = await SocialFundApplicationTypeService.getApplicationTypes();
  const types = res?.data?.["hydra:member"] || [];
  applicationTypeOptions.value = types.map((type) => ({ value: type.code, label: type.name }));
};

const loadCallPeriods = async () => {
  const res = await SocialFundCallPeriodService.getCallPeriods();
  const periods = res?.data?.["hydra:member"] || (Array.isArray(res?.data) ? res.data : []);
  callPeriodOptions.value = periods.map((period) => ({ value: period.id, label: period.label || period.year }));
};

onMounted(() => {
  loadRequest();
  loadApplicationTypes().catch(async err => Notification.addNotification(await apiErrorMessage(err, "No se han podido cargar las modalidades."), "error", 6000));
  loadCallPeriods().catch(async err => Notification.addNotification(await apiErrorMessage(err, "No se han podido cargar las convocatorias."), "error", 6000));
});

watch(
  () => route.params.id,
  (nextId, previousId) => {
    if (nextId !== previousId) {
      request.value = null; applicantHistory.value = [];
      cancelEditComment(true);
      showActionDialog.value = false; showApplicantDialog.value = false; showClassificationDialog.value = false;
      showCallPeriodDialog.value = false; showDocumentValidationDialog.value = false; showDeleteDocument.value = false;
      showAgreementUpload.value = false; pendingAgreementUpload.value = null;
      evaluationAmountFocused.value = false;
      newOtherDocumentDescription.value = "";
      loadRequest();
    }
  }
);

const user = computed(() => authStore.getUserLogged);
const socialFundPermissions = computed(() => Array.isArray(user.value?.permissions) ? user.value.permissions : []);
const hasSocialFundPermission = (action) =>
  socialFundPermissions.value.includes(`fondo_social:${action}`);
const canManageSocialFund = computed(() => hasSocialFundPermission("manage"));
const canApproveSocialFund = computed(() => hasSocialFundPermission("approve"));

// Botones de acción según el estado actual del expediente (§5 de la máquina
// de estados de SocialFundApplicationWorkflowService): solo se muestra el
// botón cuya transición es válida ahora mismo.
const canStartReview = computed(() =>
  canManageSocialFund.value && request.value?.statusCode === "received"
);
const canPrepareForDirector = computed(() =>
  canManageSocialFund.value && request.value?.statusCode === "in_review_a51"
);
const canMarkApprovedForPayment = computed(() =>
  canApproveSocialFund.value && request.value?.statusCode === "approved"
);
// Validar el justificante final ya no cierra el expediente: A51 lo cierra
// a mano (POST /close_paid) cuando está pagado y el justificante validado.
const canClosePaid = computed(() =>
  canManageSocialFund.value && request.value?.statusCode === "paid"
  && (request.value?.documents || []).some((doc) => doc.documentType === "final_proof" && doc.status === "validated")
);

// Tipo de solicitud y opinión de A51 solo se pueden corregir mientras
// Dirección no haya resuelto todavía (App\Service\SocialFundApplicationWorkflowService::isEditableBeforeDirectorDecision()).
const CLASSIFICATION_EDITABLE_STATUSES = ["received", "in_review_a51", "pending_director_review"];
const canEditClassification = computed(() =>
  canManageSocialFund.value && CLASSIFICATION_EDITABLE_STATUSES.includes(request.value?.statusCode)
);

// F2 (fondo_social/v1-20260923/PLAN_FRONTEND.md): mismo guard de estado que
// la clasificación — PATCH .../call_period da 422 si Dirección ya resolvió
// (App\Service\SocialFundApplicationWorkflowService::isEditableBeforeDirectorDecision()).
const canEditCallPeriod = computed(() =>
  canManageSocialFund.value && CLASSIFICATION_EDITABLE_STATUSES.includes(request.value?.statusCode)
);

const goBack = () => {
  sessionStorage.setItem("social-fund-requests-restore-filters", "true");
  router.push({ name: "social-fund-requests" });
};
const openHistoricalApplication = (id) => router.push({ name: "social-fund-request-detail", params: { id } });

const startReview = async () => {
  if (!canStartReview.value || actionBusy.value) return;
  actionBusy.value = true;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.startReview(applicationId.value);
    Notification.success("La solicitud ha pasado a revisión de A51.", 3500);
    await loadRequest();
  } catch (err) { Notification.addNotification(await apiErrorMessage(err, "No se ha podido iniciar la revisión."), "error", 6000);
  } finally {
    actionBusy.value = false;
    uiStore.stopLoading();
  }
};

const openApplicantDialog = () => {
  showApplicantDialog.value = true;
};

const saveApplicant = async (applicant) => {
  if (!canManageSocialFund.value || isSavingApplicant.value) return;

  isSavingApplicant.value = true;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.updateApplicant(applicationId.value, applicant);
    Notification.success("Datos del solicitante actualizados correctamente.", 3500);
    showApplicantDialog.value = false;
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se han podido guardar los datos del solicitante.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    isSavingApplicant.value = false;
    uiStore.stopLoading();
  }
};

const openClassificationDialog = () => {
  showClassificationDialog.value = true;
};

const saveClassification = async (payload) => {
  if (!canEditClassification.value || isSavingClassification.value) return;

  isSavingClassification.value = true;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.updateProject(applicationId.value, payload);
    Notification.success("Clasificación actualizada correctamente.", 3500);
    showClassificationDialog.value = false;
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido guardar la clasificación.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    isSavingClassification.value = false;
    uiStore.stopLoading();
  }
};

const openCallPeriodDialog = () => {
  callPeriodDuplicateWarning.value = null;
  showCallPeriodDialog.value = true;
};

const saveCallPeriod = async ({ callPeriodId, confirmDuplicate }) => {
  if (!canEditCallPeriod.value || isSavingCallPeriod.value) return;

  isSavingCallPeriod.value = true;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.changeCallPeriod(applicationId.value, { callPeriodId, confirmDuplicate });
    Notification.success("Convocatoria actualizada correctamente.", 3500);
    callPeriodDuplicateWarning.value = null;
    showCallPeriodDialog.value = false;
    await loadRequest();
  } catch (err) {
    if (err?.response?.status === 409) {
      callPeriodDuplicateWarning.value = { message: err.response.data.message, callPeriodId };
    } else {
      const message = await apiErrorMessage(err, "No se ha podido cambiar la convocatoria.");
      Notification.addNotification(message, "error", 6000);
    }
  } finally {
    isSavingCallPeriod.value = false;
    uiStore.stopLoading();
  }
};

const prepareForDirectorReview = (resolution) => (note) =>
  SocialFundApplicationService.prepareForDirectorReview(applicationId.value, { resolution, note });

const actionDialogHandlers = {
  "prepare-direction-approved": prepareForDirectorReview("approved"),
  "prepare-direction-rejected": prepareForDirectorReview("rejected"),
  "prepare-direction-undecided": prepareForDirectorReview("undecided"),
  "approve-payment": (note) => SocialFundApplicationService.markApprovedForPayment(applicationId.value, { note }),
  "close-paid": (note) => SocialFundApplicationService.closePaid(applicationId.value, { note }),
};

const actionDialogSuccessMessages = {
  "prepare-direction-approved": "La opinión de A51 se ha enviado a Dirección correctamente.",
  "prepare-direction-rejected": "La opinión de A51 se ha enviado a Dirección correctamente.",
  "prepare-direction-undecided": "La opinión de A51 se ha enviado a Dirección correctamente.",
  "approve-payment": "La solicitud se ha aprobado para pago.",
  "close-paid": "El expediente se ha cerrado.",
};

// Refleja el guard real del backend (denyIfUnresolvedDocuments): bloquea
// preparar para Dirección y aprobar para pago mientras haya
// documentos pendientes de validar o rechazados sin resolver.
const hasUnresolvedDocuments = computed(() => (request.value?.documents || []).some((doc) =>
  doc.status !== "validated" && !(actionDialogKind.value === "approve-payment" && doc.documentType === "final_proof")
));
const actionsBlockedByDocuments = ["prepare-direction-approved", "prepare-direction-rejected", "prepare-direction-undecided", "approve-payment"];

// Refleja REQUIRED_DOCUMENT_TYPES_BY_APPLICATION_TYPE_CODE del backend:
// documentos obligatorios y validados según la modalidad, exigidos solo
// para "aprobar para pago". aportacion_economica tiene sección propia de
// facturación en su email de aprobación; prenda_deportiva y
// solicitud_neumaticos no (Grupo Andrés paga directo al proveedor).
// El justificante final se valida después de paid; no es un requisito previo al pago.
const REQUIRED_DOCUMENT_TYPES_BY_AID_TYPE_CODE = {
  aportacion_economica: ["contract", "logo_mockup", "invoice_or_certificate"],
  prenda_deportiva: ["contract", "logo_mockup"],
  solicitud_neumaticos: ["contract", "logo_mockup"],
};
const missingRequiredDocumentTypes = computed(() => {
  const requiredTypes = REQUIRED_DOCUMENT_TYPES_BY_AID_TYPE_CODE[request.value?.application?.aidTypeCode] || [];
  const validatedTypes = (request.value?.documents || [])
    .filter((doc) => doc.status === "validated")
    .map((doc) => doc.documentType);
  return requiredTypes.filter((type) => !validatedTypes.includes(type));
});

const actionDialogBlocked = computed(() => {
  if (!actionsBlockedByDocuments.includes(actionDialogKind.value)) return false;
  if (hasUnresolvedDocuments.value) return true;
  return actionDialogKind.value === "approve-payment" && missingRequiredDocumentTypes.value.length > 0;
});
const documentTypeLabels = {
  initial: "documentación inicial",
  contract: "contrato firmado",
  logo_mockup: "mockup de aplicación del logo",
  final_proof: "justificante final de la acción",
  invoice_or_certificate: "factura o certificado de donación",
  other: "otro documento",
};
const actionDialogBlockedMessage = computed(() => {
  if (hasUnresolvedDocuments.value) {
    return "Hay documentos pendientes de validar o rechazados sin resolver. Revísalos antes de continuar.";
  }
  if (missingRequiredDocumentTypes.value.length > 0) {
    const labels = missingRequiredDocumentTypes.value.map((type) => documentTypeLabels[type] || type);
    return `Faltan documentos obligatorios validados: ${labels.join(", ")}.`;
  }
  return "";
});

const openActionDialog = (kind) => {
  actionDialogKind.value = kind;
  showActionDialog.value = true;
};

const confirmActionDialog = async ({ action, comment }) => {
  const allowedByAction = { "approve-payment": canMarkApprovedForPayment, "close-paid": canClosePaid };
  if (actionBusy.value || actionDialogBlocked.value || !(allowedByAction[action] || canPrepareForDirector).value) return;
  actionBusy.value = true;
  uiStore.startLoading();
  try {
    await actionDialogHandlers[action](comment || undefined);
    Notification.success(actionDialogSuccessMessages[action], 3500);
    showActionDialog.value = false;
    await loadRequest();
  } catch (err) { Notification.addNotification(await apiErrorMessage(err, "No se ha podido completar la acción."), "error", 6000);
  } finally {
    actionBusy.value = false;
    uiStore.stopLoading();
  }
};

const openDocumentValidation = (document, status) => {
  selectedDocument.value = document;
  selectedDocumentStatus.value = status;
  showDocumentValidationDialog.value = true;
};

const reviewDocument = async ({ document, status, rejectionReason }) => {
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.validateDocument(applicationId.value, document.id, {
      validationStatus: status,
      rejectionReason,
    });
    Notification.success(`Documento ${status === "validated" ? "validado" : "rechazado"} correctamente.`, 3500);
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido guardar la validación del documento.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    uiStore.stopLoading();
  }
};

// F4 (fondo_social/v1-20260923/PLAN_FRONTEND.md): tipos fijos (slot único,
// se sustituyen al resubir) relevantes para la modalidad de la solicitud —
// "initial" siempre se ofrece (dossier inicial), el resto según
// REQUIRED_DOCUMENT_TYPES_BY_AID_TYPE_CODE ya usado para el aviso de
// "faltan documentos".
const FIXED_DOCUMENT_TYPES_BY_AID_TYPE_CODE = {
  aportacion_economica: ["initial", "contract", "logo_mockup", "final_proof", "invoice_or_certificate"],
  prenda_deportiva: ["initial", "contract", "logo_mockup", "final_proof"],
  solicitud_neumaticos: ["initial", "contract", "logo_mockup", "final_proof"],
};
const fixedDocumentSlots = computed(() => {
  const types = FIXED_DOCUMENT_TYPES_BY_AID_TYPE_CODE[request.value?.application?.aidTypeCode] || ["initial"];
  return types.map((type) => ({
    type,
    label: documentTypeLabels[type] || type,
    document: (request.value?.documents || []).find((doc) => doc.documentType === type) || null,
  }));
});

const isApplicationClosed = computed(() => request.value?.statusCode === "closed");
const isTiresApplication = computed(() => socialFundFeatures.has("tires") && request.value?.application?.aidTypeCode === "solicitud_neumaticos");
const canEditEvaluation = computed(() => canManageSocialFund.value && !["paid", "closed"].includes(request.value?.statusCode));
const confirmDocumentDeletion = document => { documentToDelete.value = document; showDeleteDocument.value = true; };
const uploadingDocumentKey = ref(null);
const newOtherDocumentDescription = ref("");

const showAgreementUpload = ref(false);
const pendingAgreementUpload = ref(null);
watch(showAgreementUpload, value => { if (!value) pendingAgreementUpload.value = null; });
const performFixedDocumentUpload = async (type, file, signatures) => {
  if (!canManageSocialFund.value || isApplicationClosed.value || uploadingDocumentKey.value) return;
  const targetId = applicationId.value;
  uploadingDocumentKey.value = type;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.uploadDocument(targetId, { documentType: type, file, signatures });
    Notification.success("Documento subido correctamente.", 3500);
    if (applicationId.value === targetId) {
      showAgreementUpload.value = false;
      await loadRequest();
    }
  } catch (err) {
    Notification.addNotification(await apiErrorMessage(err, "No se ha podido subir el documento."), "error", 6000);
  } finally {
    uploadingDocumentKey.value = null;
    uiStore.stopLoading();
  }
};
const uploadFixedDocument = async (type, event) => {
  const file = event.target.files[0];
  event.target.value = "";
  if (!file || !canManageSocialFund.value || isApplicationClosed.value || uploadingDocumentKey.value) return;
  if (type === "contract" && socialFundFeatures.has("agreement")) {
    pendingAgreementUpload.value = { type, file };
    showAgreementUpload.value = true;
    return;
  }
  await performFixedDocumentUpload(type, file);
};
const confirmAgreementUpload = async ({ signatures }) => {
  if (!pendingAgreementUpload.value || !["applicant", "director", "both"].includes(signatures)) return;
  const { type, file } = pendingAgreementUpload.value;
  await performFixedDocumentUpload(type, file, signatures);
};

const uploadNewOtherDocument = async (event) => {
  const file = event.target.files[0];
  event.target.value = "";
  if (!file) return;
  if (!newOtherDocumentDescription.value.trim()) {
    Notification.addNotification("La descripción es obligatoria para documentos de tipo \"Otros\".", "error", 6000);
    return;
  }

  uploadingDocumentKey.value = "other";
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.uploadDocument(applicationId.value, {
      documentType: "other",
      file,
      otherDocumentDescription: newOtherDocumentDescription.value.trim(),
    });
    Notification.success("Documento subido correctamente.", 3500);
    newOtherDocumentDescription.value = "";
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido subir el documento.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    uploadingDocumentKey.value = null;
    uiStore.stopLoading();
  }
};

const replaceOtherDocument = async (document, event) => {
  const file = event.target.files[0];
  event.target.value = "";
  if (!file) return;

  uploadingDocumentKey.value = `other-${document.id}`;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.uploadDocument(applicationId.value, {
      documentType: "other",
      file,
      otherDocumentDescription: document.otherDocumentDescription,
      documentId: document.id,
    });
    Notification.success("Documento sustituido correctamente.", 3500);
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido sustituir el documento.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    uploadingDocumentKey.value = null;
    uiStore.stopLoading();
  }
};

const deleteOtherDocument = async () => {
  const document = documentToDelete.value;
  if (!canManageSocialFund.value || isApplicationClosed.value || !document) return false;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.deleteDocument(applicationId.value, document.id);
    Notification.success("Documento eliminado correctamente.", 3500);
    await loadRequest();
    return true;
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido eliminar el documento.");
    Notification.addNotification(message, "error", 6000);
    return false;
  } finally {
    uiStore.stopLoading();
  }
};

// Formato español (1.234,56) al mostrar el importe aprobado; el valor
// numérico real (con punto decimal) se mantiene en evaluationAmountInput,
// que es lo que se envía al backend.
const evaluationAmountFocused = ref(false);
const evaluationAmountDisplay = computed({
  get: () => {
    if (evaluationAmountFocused.value || evaluationAmountInput.value === null || evaluationAmountInput.value === "") {
      return evaluationAmountInput.value ?? "";
    }
    const num = Number(evaluationAmountInput.value);
    return Number.isNaN(num)
      ? evaluationAmountInput.value
      : new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  },
  set: (value) => {
    evaluationAmountInput.value = value;
  },
});

const onEvaluationAmountFocus = () => {
  evaluationAmountFocused.value = true;
};

const onEvaluationAmountBlur = () => {
  evaluationAmountFocused.value = false;
  try { evaluationAmountInput.value = normalizeEvaluationAmount(evaluationAmountInput.value); }
  catch (err) { Notification.warning(err.message, 5000); }
};

const saveEvaluation = async () => {
  if (!canEditEvaluation.value || savingEvaluation.value) return;
  let payload;
  try {
    if (isTiresApplication.value) {
      if (!isValidTires(approvedTiresInput.value)) throw new Error("Selecciona una cantidad par de 2 a 16.");
      payload = { approvedTires: approvedTiresInput.value };
    } else payload = { suggestedAmount: normalizeEvaluationAmount(evaluationAmountInput.value) };
  } catch (err) { Notification.warning(err.message, 5000); return; }
  savingEvaluation.value = true;
  uiStore.startLoading();
  try {
    await SocialFundApplicationService.updateEvaluation(applicationId.value, payload);
    Notification.success("Evaluación guardada correctamente.", 3500);
    await loadRequest();
  } catch (err) {
    const message = await apiErrorMessage(err, "No se ha podido guardar la evaluación.");
    Notification.addNotification(message, "error", 6000);
  } finally {
    savingEvaluation.value = false;
    uiStore.stopLoading();
  }
};



// El v-model:content de QuillEditor no siempre refleja visualmente el reseteo
// a "" (Quill representa "vacío" internamente como <p><br></p>) — se fuerza
// el remontaje del editor cambiando su :key tras guardar.
const commentEditorKey = ref(0);

const onCommentTextChanged = () => {
  showCommentButton.value = socialFundRichTextContent(commentText.value).length > 0;
};
watch(commentText, onCommentTextChanged);

// La autoría determina la posición; el permiso de gestión determina la edición.
const isOwnComment = (comment) =>
  comment.authorEmployeeId != null && user.value?.id != null && String(comment.authorEmployeeId) === String(user.value.id);
const internalComments = computed(() => (request.value?.comments || []).slice().sort((a, b) => {
  const first = new Date(a.createdAt).getTime();
  const second = new Date(b.createdAt).getTime();
  return Number.isFinite(first) && Number.isFinite(second) ? first - second : 0;
}));
const internalChatThread = ref(null);
useChatScroll(internalChatThread, () => {
  const last = internalComments.value[internalComments.value.length - 1];
  return JSON.stringify([applicationId.value, internalComments.value.length, last?.id, last?.comment]);
});

const startEditComment = (comment) => {
  if (savingComment.value || !canManageSocialFund.value || !isOwnComment(comment)) return;
  editingCommentId.value = comment.id;
  commentText.value = comment.comment;
  commentEditorKey.value += 1;
  showCommentButton.value = true;
};

const cancelEditComment = (force = false) => {
  if (savingComment.value && !force) return;
  editingCommentId.value = null;
  commentVisibleToDirector.value = true;
  commentText.value = "";
  commentEditorKey.value += 1;
  showCommentButton.value = false;
};

const saveComment = async () => {
  if (!canManageSocialFund.value || savingComment.value) return;
  if (!socialFundRichTextContent(commentText.value)) {
    Notification.warning("Escribe un mensaje en el chat interno.", 3500);
    return;
  }

  savingComment.value = true;
  uiStore.startLoading();
  try {
    const id = applicationId.value;
    if (editingCommentId.value) {
      await SocialFundApplicationService.editComment(id, editingCommentId.value, {
        content: commentText.value,
      });
      if (id !== applicationId.value || disposed) return;
      Notification.success("Mensaje del chat interno actualizado correctamente.", 3500);
    } else {
      await SocialFundApplicationService.addComment(id, {
        content: commentText.value,
        visibleToDirector: commentVisibleToDirector.value,
      });
      if (id !== applicationId.value || disposed) return;
      Notification.success("Mensaje del chat interno enviado correctamente.", 3500);
    }
    editingCommentId.value = null;
    commentVisibleToDirector.value = true;
    commentText.value = "";
    commentEditorKey.value += 1;
    showCommentButton.value = false;
    await loadRequest();
  } catch (err) { Notification.addNotification(await apiErrorMessage(err, "No se ha podido guardar el mensaje del chat interno. El borrador se conserva."), "error", 6000);
  } finally {
    savingComment.value = false;
    uiStore.stopLoading();
  }
};
</script>

<template>
  <main v-if="request" class="social-fund-detail">
    <HeaderTitle title="Fondo social" />

    <section class="social-fund-detail__content">
      <div class="social-fund-detail__heading">
        <button type="button" class="social-fund-detail__back" aria-label="Volver a solicitudes" @click="goBack">
          <el-icon>
            <ArrowLeft />
          </el-icon>
        </button>
        <div>
          <p>Solicitudes</p>
          <h2>Detalle de solicitud</h2>
        </div>
      </div>

      <el-card class="social-fund-detail__hero" body-style="padding:0">
        <div class="social-fund-detail__hero-content">
          <span class="social-fund-detail__hero-icon"><el-icon>
              <Document />
            </el-icon></span>
          <div class="social-fund-detail__hero-copy">
            <h3>Solicitud {{ request.reference }}</h3>
            <p>Seguimiento de la solicitud del Fondo Social</p>
          </div>
          <div class="social-fund-detail__hero-actions">
            <button v-if="canStartReview" type="button" class="btn btn_yellow" :disabled="actionBusy" @click="startReview">
              <el-icon>
                <Promotion />
              </el-icon> INICIAR REVISIÓN
            </button>
            <template v-if="canPrepareForDirector">
              <button type="button" class="btn btn_yellow social-fund-detail__report-button"
                @click="openActionDialog('prepare-direction-approved')">
                <el-icon>
                  <CircleCheckFilled />
                </el-icon> OPINIÓN A51: APROBAR
              </button>
              <button type="button" class="btn btn_red social-fund-detail__info-button"
                @click="openActionDialog('prepare-direction-rejected')">
                <el-icon>
                  <WarningFilled />
                </el-icon> OPINIÓN A51: DENEGAR
              </button>
              <button type="button" class="btn btn_yellow social-fund-detail__report-button"
                @click="openActionDialog('prepare-direction-undecided')">
                <el-icon>
                  <Promotion />
                </el-icon> OPINIÓN A51: EN DUDA
              </button>
            </template>
            <button v-if="canMarkApprovedForPayment" type="button"
              class="btn btn_yellow social-fund-detail__report-button" @click="openActionDialog('approve-payment')">
              <el-icon>
                <CircleCheckFilled />
              </el-icon> APROBAR PARA PAGO
            </button>
            <button v-if="canClosePaid" type="button"
              class="btn btn_yellow social-fund-detail__report-button" @click="openActionDialog('close-paid')">
              <el-icon>
                <CircleCheckFilled />
              </el-icon> CERRAR EXPEDIENTE
            </button>
          </div>
        </div>
      </el-card>

      <el-card class="social-fund-detail__summary" body-style="padding:10 0">
        <div class="social-fund-detail__summary-item">
          <span>Referencia</span>
          <strong>{{ request.reference }}</strong>
        </div>
        <div class="social-fund-detail__summary-item social-fund-detail__summary-item--call">
          <span>Convocatoria</span>
          <strong>{{ request.call }}
            <button v-if="canEditCallPeriod" type="button" class="btn btn_yellow social-fund-detail__call-period-edit"
              aria-label="Cambiar convocatoria" title="Cambiar convocatoria" @click="openCallPeriodDialog">
              <el-icon>
                <EditPen />
              </el-icon>
            </button>
          </strong>
        </div>
        <div class="social-fund-detail__summary-item social-fund-detail__summary-item--type">
          <span>Tipo de solicitud</span>
          <strong>{{ request.application.aidType || "—" }}</strong>
        </div>
        <div v-if="request.application.aidTypeCode === 'aportacion_economica'" class="social-fund-detail__summary-item">
          <span>Cuantía solicitada</span>
          <strong>{{ request.application.requestedAmount }}</strong>
          <span class="social-fund-detail__approved-amount-label">Importe aprobado</span>
          <strong>{{ request.application.approvedAmount }}</strong>
        </div>
        <div class="social-fund-detail__summary-item social-fund-detail__summary-item--status">
          <span>Estado actual</span>
          <b class="social-fund-detail__status" :style="getSocialFundStatusStyle(request.statusCode)"><i></i>{{ request.status }}</b><small v-if="socialFundFeatures.has('agreement')" class="social-fund-detail__agreement" :title="agreementDates(request)">{{ agreementLabel(request) }}<span v-if="agreementDates(request)" class="social-fund-detail__agreement-dates">{{ agreementDates(request) }}</span></small>
          <small v-if="request.a51ResolutionLabel" class="social-fund-detail__a51-resolution"
            :class="`social-fund-detail__a51-resolution--${request.a51ResolutionCode}`">
            Opinión de A51 (no vinculante): <strong>{{ request.a51ResolutionLabel }}</strong>
          </small>
        </div>
        <div class="social-fund-detail__summary-item">
          <span>Fecha de recepción</span>
          <strong><el-icon>
              <Calendar />
            </el-icon>{{ request.receivedAt }}</strong>
        </div>
        <div class="social-fund-detail__summary-item social-fund-detail__summary-item--days">
          <span>Días en el estado</span>
          <strong><el-icon>
              <Clock />
            </el-icon>{{ request.daysInStatus === null ? "—" : request.daysInStatus + " días" }}</strong>
        </div>
        <button v-if="canEditClassification" type="button" class="btn btn_yellow social-fund-detail__classification-edit"
          aria-label="Editar solicitud" @click="openClassificationDialog">
          <el-icon>
            <EditPen />
          </el-icon> EDITAR
        </button>
      </el-card>

      <div class="row social-fund-detail__layout">
        <div class="col-12 col-xl-5 social-fund-detail__left-column">
          <el-card class="social-fund-detail__card social-fund-detail__applicant-card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <User />
                </el-icon></span>
              <h3>Solicitante / Contacto</h3>
              <button v-if="canManageSocialFund" type="button" class="btn btn_yellow social-fund-detail__edit-applicant"
                aria-label="Editar solicitante" @click="openApplicantDialog">
                <el-icon>
                  <EditPen />
                </el-icon> EDITAR
              </button>
            </div>
            <div class="social-fund-detail__data-grid">
              <p><small>Nombre</small>{{ request.applicant.name || "—" }}</p>
              <p><small>CIF / NIF</small>{{ request.applicant.taxId || "—" }}</p>
              <p><small>Localidad</small>{{ request.applicant.locality || "—" }}</p>
              <p><small>Provincia</small>{{ request.applicant.province || "—" }}</p>
              <p><small>Correo de contacto</small>{{ request.applicant.email || "—" }}</p>
              <p><small>Teléfono de contacto</small>{{ request.applicant.phone || "—" }}</p>
              <p><small>Persona de contacto</small>{{ request.applicant.contactPerson || "—" }}</p>
              <template v-if="request.applicant.representativeName || request.applicant.representativeTaxId">
                <p><small>Representante</small>{{ request.applicant.representativeName || "—" }}</p>
                <p><small>NIF del representante</small>{{ request.applicant.representativeTaxId || "—" }}</p>
              </template>
            </div>
          </el-card>

          <el-card class="social-fund-detail__card social-fund-detail__applicant-history-card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <Clock />
                </el-icon></span>
              <h3>Histórico del solicitante</h3>
            </div>
            <p v-if="!applicantHistory.length" class="social-fund-detail__empty-history">
              No constan otras solicitudes de este solicitante.
            </p>
            <div v-else class="social-fund-detail__applicant-history">
              <button v-for="application in applicantHistory" :key="application.id" type="button"
                class="social-fund-detail__applicant-history-row" @click="openHistoricalApplication(application.id)">
                <span class="social-fund-detail__applicant-history-main">
                  <strong>{{ application.reference }}</strong>
                  <small>{{ application.aidType }}</small>
                </span>
                <span class="social-fund-detail__applicant-history-status" :style="getSocialFundStatusStyle(application.statusCode)">{{ application.status }}</span>
                <span class="social-fund-detail__applicant-history-meta">
                  <span><small>Convocatoria</small>{{ application.call }}</span>
                  <span><small>Fecha</small>{{ application.createdAt }}</span>
                  <span><small>Importe</small>{{ application.amount }}</span>
                </span>
              </button>
            </div>
          </el-card>

          <el-card class="social-fund-detail__card social-fund-detail__project-card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <Document />
                </el-icon></span>
              <h3>Proyecto / Solicitud</h3>
            </div>
            <div class="social-fund-detail__data-grid">
              <p><small>Tipo de solicitud</small>{{ request.application.aidType }}</p>
              <p><small>Entidad</small>{{ request.application.entityName || "—" }}</p>
              <p v-if="isTiresApplication"><small>Neumáticos solicitados</small>{{ request.application.requestedTires ?? "—" }}</p><p v-else><small>Importe solicitado</small>{{ request.application.requestedAmount }}</p>
              <p><small>Justificación del importe</small>{{ request.application.amountJustification || "—" }}</p>
              <p class="social-fund-detail__data-grid-full"><small>Descripción</small>{{ request.application.description
              }}</p>
            </div>
          </el-card>

          <el-card class="social-fund-detail__card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <Star />
                </el-icon></span>
              <h3>{{ isTiresApplication ? "Neumáticos concedidos" : "Importe aprobado" }}</h3>
            </div>
            <div class="social-fund-detail__evaluation">
              <p class="social-fund-detail__evaluation-requested">
                <small>{{ isTiresApplication ? "Neumáticos solicitados" : "Importe solicitado" }}</small>{{ isTiresApplication ? (request.application.requestedTires ?? "—") : request.application.requestedAmount }}
              </p>
              <div class="social-fund-detail__evaluation-form">
                <div class="form-item">
                  <template v-if="isTiresApplication"><label for="approved-tires">Neumáticos concedidos</label><el-select id="approved-tires" v-model="approvedTiresInput" class="w-100" :disabled="!canEditEvaluation || savingEvaluation"><el-option v-for="number in tireOptions" :key="number" :value="number" :label="String(number)" /></el-select></template><label v-else for="evaluation-amount">Importe aprobado (€)</label>
                  <el-input v-if="!isTiresApplication" id="evaluation-amount" v-model="evaluationAmountDisplay" class="w-100" placeholder="0,00"
                    :disabled="!canEditEvaluation || savingEvaluation" @focus="onEvaluationAmountFocus" @blur="onEvaluationAmountBlur" />
                </div>
              </div>
              <button v-if="canEditEvaluation" type="button" :disabled="savingEvaluation" class="btn btn_yellow social-fund-detail__evaluation-save" @click="saveEvaluation">{{ isTiresApplication ? "GUARDAR NEUMÁTICOS CONCEDIDOS" : "GUARDAR IMPORTE APROBADO" }}</button>
            </div>
          </el-card>

          <el-card class="social-fund-detail__card social-fund-detail__history-card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <Clock />
                </el-icon></span>
              <h3>Historial de estados</h3>
            </div>
            <ol class="social-fund-detail__timeline">
              <li v-for="(item, index) in request.history" :key="`${item.status}-${index}`"
                :style="getSocialFundStatusStyle(item.statusCode)">
                <i></i>
                <div><strong>{{ item.status }}</strong><small>{{ item.actor }}</small>
                  <p v-if="item.note" class="social-fund-detail__timeline-note">{{ item.note }}</p>
                </div>
                <time>{{ item.date }} · {{ item.time }}</time>
              </li>
            </ol>
          </el-card>
        </div>

        <div class="col-12 col-xl-7 social-fund-detail__right-column">
          <el-card class="social-fund-detail__card social-fund-detail__documents-card">
            <div class="social-fund-detail__card-heading social-fund-detail__card-heading--documents">
              <div><span><el-icon>
                    <Document />
                  </el-icon></span>
                <h3>Documentación</h3>
              </div>
              <div class="social-fund-detail__legend"><span class="validated">Validado</span><span
                  class="pending">Pendiente</span><span class="rejected">Rechazado</span></div>
            </div>
            <section class="social-fund-detail__document-group">
              <div v-if="!request.documents.length" class="social-fund-detail__data-grid">
                <p>Todavía no se ha aportado ningún documento.</p>
              </div>
              <div v-else class="social-fund-detail__document-list">
                <div v-for="document in request.documents" :key="document.id" class="social-fund-detail__document-row">
                  <span class="social-fund-detail__document-name">
                    <small v-if="document.originalName && document.documentTypeLabel"
                      class="social-fund-detail__document-type">{{ document.documentTypeLabel }}</small>
                    <span>{{ document.name }}</span>
                    <small v-if="document.otherDocumentDescription">{{ document.otherDocumentDescription }}</small>
                  </span>
                  <span class="social-fund-detail__document-status" :class="document.status"><el-icon>
                      <CircleCheckFilled v-if="document.status === 'validated'" />
                      <Clock v-else-if="document.status === 'pending'" />
                      <WarningFilled v-else />
                    </el-icon>{{ statusLabel[document.status] }}</span>
                  <span class="social-fund-detail__document-date">{{ document.uploadedAt }}</span>
                  <span class="social-fund-detail__document-reviewer">Por: {{ document.uploadedBy }}</span>
                  <span class="social-fund-detail__document-actions">
                    <template v-if="document.status === 'pending' && canManageSocialFund">
                      <button type="button" @click="openDocumentValidation(document, 'validated')">VALIDAR</button>
                      <button type="button" class="social-fund-detail__document-actions--danger"
                        @click="openDocumentValidation(document, 'rejected')">RECHAZAR</button>
                    </template>
                    <button v-else type="button" @click="openDocumentValidation(document, document.status)">VER</button>
                    <template v-if="canManageSocialFund && document.status !== 'validated' && !isApplicationClosed">
                      <label v-if="document.documentType === 'other'"
                        class="social-fund-detail__document-replace-label"
                        :class="{ 'social-fund-detail__document-replace-label--disabled': uploadingDocumentKey === `other-${document.id}` }">
                        SUSTITUIR
                        <input type="file" class="social-fund-detail__document-file-input"
                          :disabled="uploadingDocumentKey === `other-${document.id}`"
                          @change="replaceOtherDocument(document, $event)" />
                      </label>
                      <button v-if="document.documentType === 'other'" type="button"
                        class="social-fund-detail__document-actions--danger"
                        @click="confirmDocumentDeletion(document)">ELIMINAR</button>
                      <label v-else class="social-fund-detail__document-replace-label"
                        :class="{ 'social-fund-detail__document-replace-label--disabled': uploadingDocumentKey === document.documentType }">
                        SUSTITUIR
                        <input type="file" class="social-fund-detail__document-file-input"
                          :disabled="uploadingDocumentKey === document.documentType"
                          @change="uploadFixedDocument(document.documentType, $event)" />
                      </label>
                    </template>
                  </span>
                </div>
              </div>
            </section>

            <section v-if="canManageSocialFund && !isApplicationClosed" class="social-fund-detail__document-upload">
              <div v-if="fixedDocumentSlots.some((slot) => !slot.document)" class="social-fund-detail__document-upload-fixed">
                <p class="social-fund-detail__document-upload-hint">Subir documentación pendiente:</p>
                <label v-for="slot in fixedDocumentSlots.filter((s) => !s.document)" :key="slot.type"
                  class="social-fund-detail__document-upload-button"
                  :class="{ 'social-fund-detail__document-upload-button--disabled': uploadingDocumentKey === slot.type }">
                  SUBIR {{ slot.label.toUpperCase() }}
                  <input type="file" class="social-fund-detail__document-file-input"
                    :disabled="uploadingDocumentKey === slot.type" @change="uploadFixedDocument(slot.type, $event)" />
                </label>
              </div>

              <div class="social-fund-detail__document-upload-other">
                <p class="social-fund-detail__document-upload-hint">Añadir documento de tipo "Otros":</p>
                <div class="social-fund-detail__document-upload-other-form">
                  <el-input v-model="newOtherDocumentDescription" placeholder="Descripción del documento"
                    :disabled="uploadingDocumentKey === 'other'" />
                  <label class="social-fund-detail__document-upload-button"
                    :class="{ 'social-fund-detail__document-upload-button--disabled': uploadingDocumentKey === 'other' }">
                    AÑADIR DOCUMENTO
                    <input type="file" class="social-fund-detail__document-file-input"
                      :disabled="uploadingDocumentKey === 'other'" @change="uploadNewOtherDocument" />
                  </label>
                </div>
              </div>
            </section>
          </el-card>

          <el-card class="social-fund-detail__card social-fund-detail__comments-card">
            <div class="social-fund-detail__card-heading">
              <span><el-icon>
                  <ChatDotRound />
                </el-icon></span>
              <h3>Chat interno <small>(no se envían al solicitante)</small></h3>
            </div>
            <div class="social-fund-detail__ticket-messages">
              <p v-if="!internalComments.length" class="social-fund-detail__internal-chat-empty">Todavía no hay mensajes en el chat interno.</p>
              <ol ref="internalChatThread" class="social-fund-detail__internal-chat" aria-label="Conversación del chat interno">
                <li v-for="comment in internalComments" :key="comment.id" class="social-fund-detail__internal-message social-fund-chat-message"
                  :class="{ 'social-fund-detail__internal-message--own': isOwnComment(comment) }">
                  <header class="social-fund-detail__internal-message-heading">
                    <strong>{{ comment.author }}</strong>
                    <button v-if="canManageSocialFund && isOwnComment(comment)" type="button" class="social-fund-detail__internal-message-edit"
                      :disabled="savingComment" :aria-label="'Editar mensaje de ' + comment.author" @click="startEditComment(comment)">Editar</button>
                  </header>
                  <small v-if="comment.visibleToDirector === false" class="social-fund-detail__internal-note">Nota interna de A51</small>
                  <SharedExpandableContent :content-key="applicationId + '-' + comment.comment">
                    <SocialFundRichText :content="comment.comment" />
                  </SharedExpandableContent>
                  <time :datetime="comment.createdAt">{{ formatDate(comment.createdAt) }} <template v-if="comment.createdAt">· {{ formatTime(comment.createdAt) }}</template></time>
                </li>
              </ol>
              <div v-if="canManageSocialFund" class="social-fund-detail__ticket-comment-form">
                <label id="internal-chat-content-label">Mensaje para el chat interno</label>
                <SocialFundMessageEditor :key="commentEditorKey" v-model="commentText"
                  label-id="internal-chat-content-label" :disabled="savingComment" />
                <div class="social-fund-detail__comment-actions">
                  <el-checkbox v-if="!editingCommentId" v-model="commentVisibleToDirector" :disabled="savingComment">{{ socialFundFeatures.has("comments") ? "Visible para Dirección y Administración; Dirección recibe aviso por email (desmarcar para nota interna de A51)" : "Visible para Dirección (desmarcar para nota interna de A51)" }}</el-checkbox>
                  <span v-else class="social-fund-detail__comment-editing-notice">Editando mensaje del chat interno</span>
                  <div class="social-fund-detail__comment-buttons" :class="{ 'social-fund-detail__comment-buttons--editing': editingCommentId }">
                  <button v-if="editingCommentId" type="button" class="btn btn_yellow social-fund-detail__comment-button"
                    @click="cancelEditComment()" :disabled="savingComment">CANCELAR</button>
                  <button type="button" :disabled="!showCommentButton || savingComment"
                    class="btn btn_red social-fund-detail__comment-button" @click="saveComment">
                    <img width="21" height="25" class="mr-2" src="@/assets/icons/message-white.svg" alt="" />{{
                      editingCommentId ?
                        "GUARDAR CAMBIOS" : "COMENTAR" }}
                  </button>
                  </div>
                </div>
              </div>
            </div>
          </el-card>

          <SocialFundApplicantChat v-if="socialFundFeatures.has('messages')" :key="request.id" :application-id="request.id" :status="request.statusCode" :chat-open="request.chatOpen" :can-send="canManageSocialFund" />
        </div>
      </div>
    </section>

    <SocialFundAgreementUploadDialog v-model="showAgreementUpload" :file-name="pendingAgreementUpload?.file.name || ''" :saving="uploadingDocumentKey === 'contract'" @confirm="confirmAgreementUpload" />
    <SharedDeleteConfirmDialog v-model="showDeleteDocument" title="Eliminar documento" message="¿Eliminar el documento seleccionado?" :confirm-action="deleteOtherDocument" />
    <SocialFundRequestActionDialog v-model="showActionDialog" :reference="request.reference" :action="actionDialogKind"
      :saving="actionBusy" :blocked="actionDialogBlocked" :blocked-message="actionDialogBlockedMessage" @confirm="confirmActionDialog" />
    <SocialFundDocumentValidationDialog v-model="showDocumentValidationDialog" :reference="request.reference"
      :document="selectedDocument" :initial-status="selectedDocumentStatus" :read-only="!canManageSocialFund"
      @confirm="reviewDocument" />
    <SocialFundContactDialog v-model="showApplicantDialog" :reference="request.reference" :saving="isSavingApplicant"
      :applicant="request.applicant" @save="saveApplicant" />
    <SocialFundClassificationDialog v-model="showClassificationDialog" :reference="request.reference"
      :application-type-code="request.application.aidTypeCode" :a51-resolution-code="request.a51ResolutionCode"
      :requested-tires="request.application.requestedTires" :requested-amount="request.application.requestedAmountRaw" :entity-name="request.application.entityName"
      :project-description="request.application.description"
      :amount-justification="request.application.amountJustification"
      :application-type-options="applicationTypeOptions" :saving="isSavingClassification"
      @save="saveClassification" />
    <SocialFundChangeCallPeriodDialog v-model="showCallPeriodDialog" :reference="request.reference"
      :current-call-period-id="request.callPeriodId" :call-period-options="callPeriodOptions"
      :saving="isSavingCallPeriod" :duplicate-warning="callPeriodDuplicateWarning" @save="saveCallPeriod" />
  </main>
  <main v-else class="social-fund-detail"><HeaderTitle title="Fondo social" /><p v-if="loadError" role="alert">{{ loadError }}</p><button v-if="loadError" type="button" class="btn btn_yellow" @click="loadRequest">REINTENTAR</button></main>
</template>

<style lang="scss" scoped>
.social-fund-detail__agreement-dates { display: block; white-space: pre-line; margin-top: 4px; font-size: 11px; }

.social-fund-detail__agreement, .social-fund-detail__internal-note { display: inline-block; background: #eff0ff; color: #474ce6; border-radius: 10px; padding: 5px 8px; }
.social-fund-detail__comment-actions :deep(.el-checkbox) { align-items: flex-start; height: auto; min-width: 0; margin-right: 0; white-space: normal; }
.social-fund-detail__comment-actions :deep(.el-checkbox__input) { margin-top: 3px; }
.social-fund-detail__comment-actions :deep(.el-checkbox__label) { white-space: normal; overflow-wrap: anywhere; }

.social-fund-detail {
  color: #263052;
}

.social-fund-detail__content {
  padding-bottom: 26px;
}

.social-fund-detail__heading {
  align-items: center;
  display: flex;
  gap: 12px;
  margin: 0 0 14px;
}

.social-fund-detail__heading p {
  color: #838aa2;
  font-size: 13px;
  margin: 0 0 2px;
}

.social-fund-detail__heading h2 {
  color: $bg-blue;
  font-size: 2.35em;
  font-weight: 900;
  letter-spacing: -.05em;
  margin: 0;
}

.social-fund-detail__back {
  align-items: center;
  background: #fff;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(33, 43, 83, .14);
  color: $bg-blue;
  display: inline-flex;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.social-fund-detail__hero {
  background: linear-gradient(105deg, #565ce9, #3531c5) !important;
  border: 0;
  border-radius: 15px;
  overflow: hidden;
}

.social-fund-detail__hero-content {
  align-items: center;
  color: #fff;
  display: flex;
  gap: 16px;
  min-height: 106px;
  padding: 20px 26px;
}

.social-fund-detail__hero-icon,
.social-fund-detail__card-heading>span {
  align-items: center;
  background: #edeeff;
  border-radius: 13px;
  color: $bg-blue;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 25px;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.social-fund-detail__hero-icon {
  background: rgba(255, 255, 255, .18);
  color: #fff;
}

.social-fund-detail__hero-copy h3 {
  font-size: 23px;
  font-weight: 900;
  margin: 0;
}

.social-fund-detail__hero-copy p {
  font-size: 14px;
  margin: 2px 0 0;
  opacity: .92;
}

.social-fund-detail__hero-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.social-fund-detail__hero-actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  padding: 8px 16px;
}

.social-fund-detail__report-button {
  color: $bg-blue !important;
}

.social-fund-detail__info-button {
  color: #fff !important;
}

.social-fund-detail__summary {
  background: #fff !important;
  border-radius: 15px;
  margin-top: 16px;
  padding-right: 0;
  position: relative;
}

.social-fund-detail__classification-edit {
  align-items: center;
  color: $bg-blue !important;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 5px;
  min-height: 34px;
  padding: 6px 10px;
  align-self: center;
  flex: 0 0 auto;
  margin: 0 16px 0 auto !important;
  width: auto !important;
}

.social-fund-detail__classification-edit .el-icon {
  font-size: 15px;
}

.social-fund-detail__summary :deep(.el-card__body) {
  display: flex;
  flex-wrap: wrap;
  padding: 14px 0;
  row-gap: 18px;
}

.social-fund-detail__summary-item {
  border-right: 1px solid #e4e6ef;
  display: flex;
  flex: 1 1 150px;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 0 16px;
}

.social-fund-detail__summary-item--call {
  flex: 1.2 1 200px;
}

.social-fund-detail__summary-item--type {
  flex: 1.25 1 175px;
}

.social-fund-detail__summary-item--status {
  flex: 1.8 1 250px;
}

.social-fund-detail__summary-item--days {
  flex: .7 1 125px;
}

.social-fund-detail__approved-amount-label {
  margin-top: 10px;
}

.social-fund-detail__summary-item:last-of-type {
  border-right: 0;
}

.social-fund-detail__summary-item>span,
.social-fund-detail small {
  color: #737b94;
  display: block;
  font-size: 12px;
  font-weight: 500;
}

.social-fund-detail__summary-item strong {
  align-items: center;
  color: #27304f;
  display: flex;
  font-size: 17px;
  gap: 7px;
}

.social-fund-detail__call-period-edit.btn {
  align-items: center;
  border: 0;
  border-radius: 50%;
  color: $bg-blue !important;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 28px;
  font-size: 14px;
  height: 28px;
  justify-content: center;
  margin: 0 !important;
  min-height: 28px;
  min-width: 28px;
  padding: 0;
  width: 28px !important;
}

.social-fund-detail__call-period-edit:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 2px;
}

.social-fund-detail__status {
  align-items: center;
  max-width: 100%;
  background: var(--status-bg);
  border: 1px solid var(--status-border);
  border-radius: 15px;
  color: var(--status-text);
  display: inline-flex;
  font-size: 12px;
  gap: 6px;
  padding: 5px 10px;
  width: fit-content;
}

.social-fund-detail__status i {
  flex-shrink: 0;
  background: var(--status-dot);
  border-radius: 50%;
  height: 7px;
  width: 7px;
}


.social-fund-detail__a51-resolution {
  color: #99a0b3;
  font-size: 11px;
  font-weight: 500;
}

.social-fund-detail__a51-resolution strong {
  font-weight: 700;
}

.social-fund-detail__a51-resolution--approved strong {
  color: #16a34a;
}

.social-fund-detail__a51-resolution--rejected strong {
  color: #f9156f;
}

.social-fund-detail__a51-resolution--undecided strong {
  color: #a87300;
}

.social-fund-detail__layout {
  margin-top: 14px;
}

.social-fund-detail__left-column,
.social-fund-detail__right-column {
  display: grid;
  gap: 14px;
}

.social-fund-detail__card {
  background: #fff !important;
  border-radius: 15px;
}

.social-fund-detail__card :deep(.el-card__body) {
  padding: 17px 18px;
}

.social-fund-detail__card-heading {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 15px;
}

.social-fund-detail__card-heading h3 {
  color: #202845;
  font-size: 18px;
  font-weight: 900;
  margin: 0;
}

.social-fund-detail__card-heading h3 small {
  color: #747d9f;
  display: inline;
  font-size: 13px;
  font-weight: 600;
}

.social-fund-detail__edit-applicant {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 5px;
  margin-left: auto;
  min-height: 34px;
  padding: 6px 10px;
  width: auto !important;
}

.social-fund-detail__edit-applicant .el-icon {
  font-size: 15px;
}

.social-fund-detail__edit-applicant {
  color: $bg-blue !important;
}

.social-fund-detail__card-heading>span {
  font-size: 22px;
  height: 42px;
  width: 42px;
}

.social-fund-detail__data-grid {
  display: grid;
  gap: 12px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.social-fund-detail__data-grid p {
  font-size: 13px;
  line-height: 1.35;
  margin: 0;
  word-break: break-word;
}

.social-fund-detail__applicant-card .social-fund-detail__card-heading h3,
.social-fund-detail__applicant-history-card .social-fund-detail__card-heading h3,
.social-fund-detail__project-card .social-fund-detail__card-heading h3,
.social-fund-detail__history-card .social-fund-detail__card-heading h3 {
  font-size: 19px;
}

.social-fund-detail__applicant-card .social-fund-detail__data-grid p,
.social-fund-detail__project-card .social-fund-detail__data-grid p {
  font-size: 14px;
}

.social-fund-detail__data-grid-full {
  grid-column: 1 / -1;
}

.social-fund-detail__empty-history {
  color: #737b94;
  font-size: 14px;
  margin: 0;
}

.social-fund-detail__applicant-history {
  border: 1px solid #dde0e9;
  border-radius: 9px;
  overflow: hidden;
}

.social-fund-detail__applicant-history-row {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #e8eaf0;
  color: #263052;
  cursor: pointer;
  display: grid;
  font-size: 13px;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}

.social-fund-detail__applicant-history-row:last-child {
  border-bottom: 0;
}

.social-fund-detail__applicant-history-row:hover,
.social-fund-detail__applicant-history-row:focus-visible {
  background: #f7f7ff;
  outline: 2px solid $bg-blue;
  outline-offset: -2px;
}

.social-fund-detail__applicant-history-row>span,
.social-fund-detail__applicant-history-main {
  min-width: 0;
}

.social-fund-detail__applicant-history-meta {
  display: grid;
  gap: 10px;
  grid-column: 1 / -1;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.social-fund-detail__applicant-history-row strong,
.social-fund-detail__applicant-history-row small {
  display: block;
}

.social-fund-detail__applicant-history-row strong {
  color: $bg-blue;
  font-size: 14px;
}

.social-fund-detail__applicant-history-row small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.social-fund-detail__applicant-history-status {
  background: var(--status-bg);
  border: 1px solid var(--status-border);
  border-radius: 15px;
  color: var(--status-text);
  font-size: 12px;
  padding: 4px 8px;
  width: fit-content;
}

.social-fund-detail__link {
  align-items: center;
  background: transparent;
  border: 0;
  color: $bg-blue;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 3px;
  margin: 13px 0 0 auto;
  padding: 0;
}

.social-fund-detail__evaluation-requested {
  color: #27304f;
  font-size: 14px;
  margin: 0 0 16px;
}

.social-fund-detail__evaluation-form {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
}

.social-fund-detail__evaluation-form .form-item label {
  color: #283150;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 6px;
}

.social-fund-detail__evaluation-form :deep(.el-input-number__increase) {
  border-bottom-right-radius: 20px;
  border-top-right-radius: 20px;
}

.social-fund-detail__evaluation-form :deep(.el-input-number__decrease) {
  border-bottom-left-radius: 20px;
  border-top-left-radius: 20px;
}

.social-fund-detail__evaluation-save {
  margin-top: 16px !important;
  width: 100%;
}

.social-fund-detail__timeline {
  list-style: none;
  margin: 2px 0 0 9px;
  padding: 0 0 0 21px;
  position: relative;
}

.social-fund-detail__timeline::before {
  background: #d9dce5;
  bottom: 12px;
  content: "";
  left: 0;
  position: absolute;
  top: 7px;
  width: 1px;
}

.social-fund-detail__timeline li {
  display: grid;
  gap: 3px 12px;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 0 0 17px;
  position: relative;
}

.social-fund-detail__timeline li>i {
  background: var(--status-dot, #a8adb9);
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px var(--status-dot, #c6cad5);
  height: 12px;
  left: -27px;
  position: absolute;
  top: 3px;
  width: 12px;
}





.social-fund-detail__timeline strong,
.social-fund-detail__timeline small {
  display: block;
  font-size: 13px;
}

.social-fund-detail__timeline time {
  color: #737b94;
  font-size: 12px;
  white-space: nowrap;
}

.social-fund-detail__timeline-note {
  color: #4c5470;
  font-size: 13px;
  margin: 4px 0 0;
}

.social-fund-detail__card-heading--documents {
  justify-content: space-between;
}

.social-fund-detail__card-heading--documents>div:first-child {
  align-items: center;
  display: flex;
  gap: 12px;
}

.social-fund-detail__legend {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.social-fund-detail__legend span {
  align-items: center;
  color: #5f6679;
  display: inline-flex;
  font-size: 11px;
  gap: 5px;
}

.social-fund-detail__legend span::before {
  border-radius: 50%;
  content: "";
  height: 10px;
  width: 10px;
}

.social-fund-detail__legend .validated::before {
  background: #16a34a;
}

.social-fund-detail__legend .pending::before {
  background: #f4b400;
}

.social-fund-detail__legend .rejected::before {
  background: #f9156f;
}

.social-fund-detail__document-group+.social-fund-detail__document-group {
  margin-top: 14px;
}

.social-fund-detail__document-group h4 {
  align-items: center;
  color: #27304f;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 9px;
  margin: 0 0 7px;
}

.social-fund-detail__document-group h4 b {
  align-items: center;
  background: #f7d20d;
  border-radius: 50%;
  color: #202845;
  display: inline-flex;
  font-size: 12px;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.social-fund-detail__document-list {
  border: 1px solid #dde0e9;
  border-radius: 9px;
  overflow: hidden;
}

.social-fund-detail__document-row {
  align-items: center;
  border-bottom: 1px solid #e8eaf0;
  display: grid;
  font-size: 11px;
  gap: 10px;
  grid-template-columns: minmax(150px, 1fr) 104px 72px 115px auto;
  min-height: 35px;
  padding: 6px 12px;
}

.social-fund-detail__document-row:last-child {
  border-bottom: 0;
}

.social-fund-detail__document-name {
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.social-fund-detail__document-name small {
  color: #8a90a5;
  font-size: 10px;
}

.social-fund-detail__document-name .social-fund-detail__document-type {
  color: #5c67b5;
  font-size: inherit;
  font-weight: 700;
  line-height: 1.35;
}

.social-fund-detail__document-status {
  align-items: center;
  display: inline-flex;
  gap: 5px;
}

.social-fund-detail__document-status.validated {
  color: #168b45;
}

.social-fund-detail__document-status.pending {
  color: #a87900;
}

.social-fund-detail__document-status.rejected {
  color: #d81f61;
}

.social-fund-detail__document-date,
.social-fund-detail__document-reviewer {
  color: #747b8e;
}

.social-fund-detail__document-reviewer {
  white-space: nowrap;
}

.social-fund-detail__document-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.social-fund-detail__document-actions button {
  background: #fff;
  border: 1px solid $bg-blue;
  border-radius: 4px;
  color: $bg-blue;
  font-size: 9px;
  font-weight: 800;
  padding: 3px 8px;
}

.social-fund-detail__document-actions button:last-child {
  border-color: #f9156f;
  color: #f9156f;
}

.social-fund-detail__document-actions .social-fund-detail__document-actions--danger {
  border-color: #f9156f;
  color: #f9156f;
}

.social-fund-detail__document-replace-label,
.social-fund-detail__document-upload-button {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  position: relative;
}

.social-fund-detail__document-replace-label {
  background: #fff;
  border: 1px solid $bg-blue;
  border-radius: 4px;
  color: $bg-blue;
  font-size: 9px;
  font-weight: 800;
  padding: 3px 8px;
}

.social-fund-detail__document-replace-label--disabled,
.social-fund-detail__document-upload-button--disabled {
  cursor: not-allowed;
  opacity: .5;
}

.social-fund-detail__document-file-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.social-fund-detail__document-upload {
  border-top: 1px solid #e8eaf0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 16px;
  padding-top: 16px;
}

.social-fund-detail__document-upload-hint {
  color: #27304f;
  font-size: 13px;
  font-weight: 800;
  margin: 0 0 10px;
}

.social-fund-detail__document-upload-fixed {
  align-items: stretch;
  display: inline-flex;
  flex-direction: column;
  gap: 8px;
  width: max-content;
}

.social-fund-detail__document-upload-button {
  background: #f5f3ff;
  border: 1px solid $bg-blue;
  border-radius: 20px;
  color: $bg-blue;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  justify-content: center;
  padding: 6px 14px;
}

.social-fund-detail__document-upload-other-form {
  align-items: center;
  display: flex;
  gap: 10px;
}

.social-fund-detail__document-upload-other-form .el-input {
  flex: 1;
}

.social-fund-detail__documents-link {
  margin-top: 17px;
}

.social-fund-detail__ticket-messages { margin-top: 16px; min-width: 0; }
.social-fund-detail__internal-chat {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
}
.social-fund-detail__internal-message {
  align-self: flex-start;
  min-width: 0;
  max-width: 90%;
  padding: 12px;
  border-radius: 12px;
  background: #f4f4f6;
  overflow-wrap: anywhere;
}
.social-fund-detail__internal-message--own { align-self: flex-end; background: #eff0ff; }
.social-fund-detail__internal-message-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 4px 16px; }
.social-fund-detail__internal-message-edit { border: 0; background: transparent; color: #474ce6; font-size: 12px; padding: 4px; text-decoration: underline; cursor: pointer; }
.social-fund-detail__internal-message-edit:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; border-radius: 4px; }
.social-fund-detail__internal-message-edit:disabled { opacity: .5; cursor: not-allowed; }
.social-fund-detail__internal-message time { display: block; }
.social-fund-detail__internal-message .social-fund-detail__internal-note { margin-top: 4px; padding: 3px 6px; background: #fff; font-size: 11px; }
.social-fund-detail__ticket-messages :deep(.ql-editor :where(p, ul, ol, li, b, strong, em, i, u, s, a, span:not(.ql-size-small):not(.ql-size-large):not(.ql-size-huge))) { font-size: inherit; }
@media (max-width: 767px) {
  .social-fund-detail__internal-message-edit { min-height: 44px; }
}

.social-fund-detail__ticket-comment-form {
  padding-top: 14px;
}


.social-fund-detail__comment-actions {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-top: 14px;
  flex-wrap: wrap;
}

.social-fund-detail__comment-editing-notice {
  color: #69718a;
  font-size: 13px;
  font-style: italic;
}

.social-fund-detail__comment-buttons { display: flex; gap: 10px; max-width: 100%; }
.social-fund-detail__comment-buttons--editing { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); width: 380px; }
.social-fund-detail__comment-editing-notice { flex-basis: 100%; }
.social-fund-detail__comment-button.btn_red { color: #fff; }
.social-fund-detail__comment-button.btn_yellow { color: #474ce6; }
.social-fund-detail__comment-button {
  align-items: center;
  gap: 8px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  height: auto;
  min-height: 44px;
  margin: 0;
  justify-content: center;
  min-width: 158px;
  padding: 6px 14px;
  width: auto;
}

.social-fund-detail__comment-button img {
  height: 19px;
  object-fit: contain;
  width: 17px;
}

.social-fund-detail__comment-button:disabled {
  cursor: not-allowed;
  opacity: .5;
}

@media (min-width: 768px) and (max-width: 1799px) {
  .social-fund-detail__document-row { grid-template-columns: minmax(0, 1fr) auto auto; }
  .social-fund-detail__document-name, .social-fund-detail__document-actions { grid-column: 1 / -1; }
  .social-fund-detail__document-actions { justify-content: flex-end; }
}

@media (max-width: 1199px) {
  .social-fund-detail__left-column {
    margin-bottom: 14px;
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .social-fund-detail__summary :deep(.el-card__body) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .social-fund-detail__summary-item--status {
    grid-column: span 2;
  }

  .social-fund-detail__classification-edit {
    justify-self: end;
  }
}

@media (max-width: 767px) {
  .social-fund-detail__card-heading {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
  }

  .social-fund-detail__card-heading h3 {
    flex: 1 1 auto;
    font-size: 14px;
    letter-spacing: -.02em;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .social-fund-detail__card-heading h3 small {
    display: block;
    margin-top: 4px;
    white-space: normal;
  }

  .social-fund-detail__edit-applicant {
    flex: 0 0 auto;
    font-size: 9px;
    gap: 4px;
    margin-left: auto;
    min-height: 34px;
    padding: 6px 8px;
    white-space: nowrap;
    width: auto !important;
  }

  .social-fund-detail__heading h2 {
    font-size: 2em;
  }

  @media (max-width: 374px) {
    .social-fund-detail__edit-applicant {
      font-size: 0;
      min-width: 42px;
      padding-left: 9px;
      padding-right: 9px;
    }

    .social-fund-detail__edit-applicant .el-icon {
      font-size: 16px;
    }
  }

  .social-fund-detail__hero-content {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 18px;
  }

  .social-fund-detail__hero-copy {
    flex: 1;
    min-width: 0;
  }

  .social-fund-detail__hero-copy h3 {
    font-size: 18px;
  }

  .social-fund-detail__hero-actions {
    flex-direction: column;
    margin-left: 0;
    width: 100%;
  }

  .social-fund-detail__hero-actions .btn {
    width: 100%;
  }

  .social-fund-detail__summary :deep(.el-card__body) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 0;
    row-gap: 0;
  }

  .social-fund-detail__classification-edit {
    grid-column: 1 / -1;
    justify-self: end;
    margin: 12px 15px !important;
  }

  .social-fund-detail__summary-item {
    border-bottom: 1px solid #e4e6ef;
    padding: 13px 15px;
  }

  .social-fund-detail__summary-item:nth-child(2n) {
    border-right: 0;
  }

  .social-fund-detail__summary-item:last-of-type,
  .social-fund-detail__summary-item:nth-last-of-type(2):nth-child(odd) {
    border-bottom: 0;
  }

  .social-fund-detail__summary-item:last-of-type:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .social-fund-detail__summary-item strong {
    font-size: 14px;
  }

  .social-fund-detail__card :deep(.el-card__body) {
    padding: 15px;
  }

  .social-fund-detail__data-grid,
  .social-fund-detail__evaluation-form {
    grid-template-columns: 1fr;
  }

  .social-fund-detail__applicant-history-meta {
    grid-template-columns: 1fr;
  }

  .social-fund-detail__card-heading--documents {
    align-items: flex-start;
    flex-direction: column;
  }

  .social-fund-detail__document-row {
    align-items: start;
    grid-template-columns: 1fr auto;
    padding: 10px;
  }

  .social-fund-detail__document-name {
    grid-column: 1 / -1;
    font-weight: 700;
  }

  .social-fund-detail__document-date,
  .social-fund-detail__document-reviewer {
    font-size: 10px;
  }

  .social-fund-detail__document-actions {
    grid-column: 1 / -1;
  }

  .social-fund-detail__ticket-messages {
    border-radius: 18px;
    padding: 12px;
  }

  .social-fund-detail__comment-actions {
    align-items: stretch;
    flex-direction: column;
    justify-content: flex-start;
  }

  .social-fund-detail__comment-actions :deep(.el-checkbox) {
    width: 100%;
  }

  .social-fund-detail__comment-buttons { width: 100%; }
  .social-fund-detail__comment-buttons--editing { gap: 8px; }
  .social-fund-detail__comment-buttons--editing .social-fund-detail__comment-button { padding: 8px; white-space: normal; }
  .social-fund-detail__comment-button {
    min-width: 0;
    width: 100%;
  }

  .social-fund-detail__timeline li {
    grid-template-columns: 1fr;
  }

  .social-fund-detail__timeline time {
    white-space: normal;
  }
}
</style>
